import React, { useContext, useMemo } from 'react'
import { Box, Checkbox, FormGroup, FormControlLabel, Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { AppContext } from '../../App'
import { selectChartConfig } from '../../selectors/chart'
import { CandlestickSeriesOptions } from '../chartV2/chart.typings'
import ColorSelector from '../colorSelector/colorSelector'
import tinycolor from 'tinycolor2'
import CommonChartSettingsTabs from './commonChartSettingsTabs'
import { chartSettingsFormGroupSx, chartSettingsSectionTitleSx } from './chartSettingsLayout'

type Props = {
    viewKey: string
}
const HighLowCandlestickSettings = ({ viewKey }: Props) => {
    const appContext = useContext(AppContext)
    const chart = appContext.getChart(viewKey)
    const chartConfig = useSelector(selectChartConfig(viewKey))
    const primarySeriesGroupId = useMemo(() => {
        return chart?.primarySeriesGroup?.config?.id
    }, [chart?.primarySeriesGroup?.config?.id])
    const color = useMemo(() => {
        let color = '#2862ff'
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.upColor ?? '#2862ff'
        }
        return tinycolor(color)
    }, [chartConfig, primarySeriesGroupId])

    const borderColor = useMemo(() => {
        let color = '#2862ff'
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.borderUpColor ?? '#2862ff'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const visible = useMemo(() => {
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            return (series as any)?.visible ?? true
        }
        return false
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const borderVisible = useMemo(() => {
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            return (series as any)?.borderVisible ?? true
        }
        return true
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const handleColorChangeSeries = (newColor: string, type: keyof CandlestickSeriesOptions) => {
        if (chart) {
            chart.primarySeriesGroup?.getPrimarySeries()?.updateOptions({
                [type]: newColor
            } as Partial<CandlestickSeriesOptions>)
        }
    }
    const handleBooleanChange = (value: boolean, type: keyof CandlestickSeriesOptions) => {
        if (chart) {
            chart.primarySeriesGroup?.getPrimarySeries()?.updateOptions({
                [type]: value
            } as Partial<CandlestickSeriesOptions>)
        }
    }
    const symbolTabContent = (
        <Box>
            <Typography variant="subtitle1" sx={chartSettingsSectionTitleSx}>
                HIGH LOW
            </Typography>
            <FormGroup sx={chartSettingsFormGroupSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControlLabel
                        sx={{ width: 100, ml: 0, '& .MuiFormControlLabel-label': { fontSize: '14px' } }}
                        control={<Checkbox checked={visible} onChange={(e) => handleBooleanChange(e.target.checked, 'visible' as any)} />}
                        label="Body"
                    />
                    <ColorSelector
                        compact
                        showOnlySwatch
                        color={color.toHexString()}
                        opacity={color.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String()
                            handleColorChangeSeries(newColor, 'upColor')
                        }}
                    />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControlLabel
                        sx={{ width: 100, ml: 0, '& .MuiFormControlLabel-label': { fontSize: '14px' } }}
                        control={<Checkbox checked={borderVisible} onChange={(e) => handleBooleanChange(e.target.checked, 'borderVisible' as any)} />}
                        label="Border"
                    />
                    <ColorSelector
                        compact
                        showOnlySwatch
                        color={borderColor.toHexString()}
                        opacity={borderColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String()
                            handleColorChangeSeries(newColor, 'borderUpColor')
                        }}
                    />
                </Box>
            </FormGroup>
        </Box>
    )

    return <CommonChartSettingsTabs viewKey={viewKey} symbolTabContent={symbolTabContent} />
}

export default HighLowCandlestickSettings
